import React, { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import api from '@disco/api/server'
import { updateShortlist } from '@disco/state-management/actions/ShortlistActions'
import { isArtistExists } from '@disco/state-management/selectors/artists'
import { getLocationBeforeTransitions } from '@disco/state-management/selectors/routing'
import { getShortlistById } from '@disco/state-management/selectors/shortlists'
import { getTextColor } from '@disco/utils/colors'

import type { Shortlist } from 'types/shortlist'

import { getShortlistBaseRoute } from 'utils/routing'

import ArtistArtwork from 'components/common/artist-artwork'
import ArtistSelection from 'components/common/artist-selection'
import Alert from 'components/ui/alert'
import Label from 'components/ui/label'
import TextField from 'components/ui/text-field'

import { useDispatch, useSelector } from 'hooks/use-redux'

const PlaylistPresentationArtistSelection = ({
  shortlistId,
  disabled,
  resetBio,
}: {
  shortlistId: string
  disabled?: boolean
  resetBio?: (..._args: Array<any>) => any
}): React.ReactNode => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [notFoundAlert, setNotFoundAlert] = useState(null)
  const [differentArtistsAlert, setDifferentArtistsAlert] = useState(null)
  const shortlist: Shortlist = useSelector(state =>
    getShortlistById(state, {
      id: shortlistId,
    }),
  )
  const location = useSelector(getLocationBeforeTransitions)
  const updateArtistRef = useRef(null)
  const { showcase } = shortlist
  const [selectedArtist, setSelectedArtist] = useState(
    showcase?.artist?.id
      ? {
          value: showcase?.artist?.id,
          label: showcase?.artist?.name,
        }
      : null,
  )
  const artists = useSelector(state => state.artists.items)
  const artistInStore = artists?.[selectedArtist?.value]
  const artistExists = useSelector(state =>
    isArtistExists(state, showcase?.artist?.id),
  )

  const updateArtist = artist => {
    if (showcase?.artist?.id !== artist?.id) {
      if (resetBio) {
        resetBio()
      }

      dispatch(
        updateShortlist(shortlistId, {
          showcase: {
            ...showcase,
            artist,
            artist_override_image_preview: null,
            artist_override_image: null,
            artist_override_image_url: null,
            artist_override_bio_html: null,
            artist_override_bio_editor: null,
            focal_points: null,
          },
        }),
      )
    }
  }

  updateArtistRef.current = updateArtist
  useEffect(() => {
    let commonArtist = shortlist.items[0].track.artist

    if (
      !selectedArtist &&
      commonArtist &&
      shortlist.items.every(({ track }) => track.artist === commonArtist)
    ) {
      ;(async () => {
        try {
          const result = await api.getArtists(commonArtist)
          const artist = result.results.find(
            ({ name }) => name === commonArtist,
          )

          if (artist) {
            if (updateArtistRef.current) {
              updateArtistRef.current(artist)
            }

            setSelectedArtist({
              label: artist.name,
              value: artist.id,
            })
          }
        } catch (_) {}
      })()
    }
  }, [])
  useEffect(() => {
    if (selectedArtist) {
      const differentArtists = !shortlist.items.some(
        ({ track }) => track.artist === selectedArtist?.label,
      )

      if (differentArtists) {
        setDifferentArtistsAlert(
          <>
            <Alert status="change" dismissible id="different_artists_alert">
              We found conflicting artist metadata for these tracks. You may
              want to <br />
              {(() => {
                const baseRoute = getShortlistBaseRoute(location)
                return (
                  <Link
                    className="text-link"
                    to={`${baseRoute}/shortlists/${shortlistId}/bulk-edit?all=1`}
                  >
                    edit the track metadata
                  </Link>
                )
              })()}{' '}
              or {/* TODO: add link */}
              <Link to="#">
                link other artist profiles to {selectedArtist.label}
              </Link>
            </Alert>
            <div className="ChooseDesign_AlertSpacer" />
          </>,
        )
      } else {
        setDifferentArtistsAlert(null)
      }
    } else {
      setDifferentArtistsAlert(null)
    }
  }, [shortlist, selectedArtist])

  const onInvalid = () => {
    const baseRoute = getShortlistBaseRoute(location)
    navigate(`${baseRoute}/shortlists/${shortlist.id}/save/presentation`)
  }

  return (
    <>
      <div className="ChooseDesign_Spacer">
        <ArtistSelection
          value={selectedArtist}
          getOptions={({ callback, input, artists }) => {
            if (artists.length === 0 && input) {
              setNotFoundAlert(
                <>
                  <Alert status="warning" icon="warning">
                    We were unable to find an artist for these tracks, <br />
                    {(() => {
                      const baseRoute = getShortlistBaseRoute(location)
                      return (
                        <Link
                          className="text-link"
                          to={`${baseRoute}/shortlists/${shortlistId}/bulk-edit?all=1`}
                        >
                          please edit the track metadata
                        </Link>
                      )
                    })()}{' '}
                    to add an artist
                  </Alert>
                  <div className="ChooseDesign_AlertSpacer" />
                </>,
              )
            } else if (notFoundAlert && input) {
              setNotFoundAlert(null)
            }

            callback(null, {
              options: artists.map(artist => ({
                label: artist.name,
                value: artist.id,
              })),
              complete: false,
            })
          }}
          onChange={value => {
            if (updateArtistRef.current) {
              updateArtistRef.current(artists[value?.value] || null)
            }

            setSelectedArtist(value)
            setNotFoundAlert(null)
          }}
          checkForApiUpdates
          disabled={disabled}
        />
        {/* TODO: add "invalid" design like in TextField for ArtistSelection*/}
        {/* https://disco-team.atlassian.net/browse/DISCO-2581 */}
        {Boolean(artistInStore?.loading || !selectedArtist?.value) && (
          <TextField
            patternErrorMessage="Please select an artist to save this showcase."
            value=""
            onInvalid={onInvalid}
            required
            hidden
          />
        )}
      </div>

      {Boolean(showcase?.artist?.id) && (
        <div className="ChooseDesign_Spacer">
          <div className="ChooseDesign_ArtistWrapper">
            {artistInStore?.loading ? (
              <Label caption="Loading..." />
            ) : (
              <>
                <div className="ChooseDesign_ArtistContainer">
                  <ArtistArtwork
                    image={showcase?.artist?.image_url}
                    imageParams={{
                      crop: 'faces',
                    }}
                    color={getTextColor(showcase?.artist?.name)}
                  />
                </div>
                {showcase?.artist?.name}
              </>
            )}
          </div>
          {/* TODO: add "invalid" design like in TextField for ArtistSelection*/}
          {/* https://disco-team.atlassian.net/browse/DISCO-2581 */}
          {!artistInStore?.loading && !artistExists && (
            <TextField
              patternErrorMessage="The artist associated with this Page has been deleted. Please choose another artist to continue."
              value=""
              onInvalid={onInvalid}
              required
              hidden
            />
          )}
        </div>
      )}

      {notFoundAlert}
      {differentArtistsAlert}
    </>
  )
}

export default PlaylistPresentationArtistSelection
